import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  Alert,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { SavedWorkspaceState } from '../../types';

interface RenameDialogProps {
  isOpen: boolean;
  savedState: SavedWorkspaceState | null;
  onConfirm: (newName: string) => void;
  onCancel: () => void;
}

export const RenameDialog: React.FC<RenameDialogProps> = ({
  isOpen,
  savedState,
  onConfirm,
  onCancel,
}) => {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && savedState) {
      setNewName(savedState.name);
      setError('');
    }
  }, [isOpen, savedState]);

  const handleConfirm = () => {
    if (!newName.trim()) {
      setError('Please enter a name for the saved state');
      return;
    }
    
    if (newName.trim() === savedState?.name) {
      setError('Please enter a different name');
      return;
    }
    
    setError('');
    onConfirm(newName.trim());
  };

  const handleCancel = () => {
    setNewName(savedState?.name || '');
    setError('');
    onCancel();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
    if (error) setError(''); // Clear error when user starts typing
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleConfirm();
    }
  };

  if (!savedState) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      title={
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
        }}>
          <EditOutlined style={{ fontSize: '20px' }} />
          Rename saved state
        </div>
      }
      okText="Rename"
      cancelText="Cancel"
      onOk={handleConfirm}
      width={400}
    >
        {error && (
          <Alert
            type="error"
            message="Error"
            description={error}
            showIcon
            style={{ marginBottom: '1rem' }}
          />
        )}
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>New name</label>
          <Input
            id="rename-input"
            placeholder="Enter a new name for this saved state"
            value={newName}
            onChange={handleNameChange}
            onKeyDown={handleKeyPress}
            autoFocus
          />
          <div style={{ fontSize: '0.75rem', opacity: 0.65, marginTop: '4px' }}>
            Current name: "{savedState.name}"
          </div>
        </div>
    </Modal>
  );
};